import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Suspense, lazy, useEffect, useRef } from "react";
import Home from "./component/Home";
import SafetyTipsBar from "./component/SafetyTipsBar";
import { trackPageView } from "./services/firebase";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function AnalyticsTracker() {
  const { pathname } = useLocation();
  useEffect(() => { trackPageView(pathname); }, [pathname]);
  return null;
}

// Route-based code splitting: only the landing page (the entry point for most
// visitors) ships in the initial bundle. Every other route loads on demand,
// shrinking the initial JS payload and improving LCP/TBT on first visit.
const LoanCalculator = lazy(() => import("./component/LoanCalculator"));
const AboutUs = lazy(() => import("./component/AboutUs"));
const FAQ = lazy(() => import("./component/FAQ"));
const ContactUs = lazy(() => import("./component/ContactUs"));
const InterestRatePolicy = lazy(() => import("./component/policies/InterestRatePolicy"));
const RiskManagementPolicy = lazy(() => import("./component/policies/RiskManagementPolicy"));
const CancellationRefundPolicy = lazy(() => import("./component/policies/CancellationRefundPolicy"));
const CorporateGovernance = lazy(() => import("./component/CorporateGovernance"));
const KYCAmlPolicy = lazy(() => import("./component/policies/KYCAmlPolicy"));
const Blogs = lazy(() => import("./component/Blogs"));
const BlogDetail = lazy(() => import("./component/BlogDetail"));
const LoanIllustrationAndRates = lazy(() => import("./component/LoanIllustrationAndRates"));
const Services = lazy(() => import("./component/Services"));
const TermsConditions = lazy(() => import("./component/TermsConditions"));
const PrivacyPolicy = lazy(() => import("./component/policies/PrivacyPolicy"));
const FairPracticeCode = lazy(() => import("./component/FairPracticeCode"));
const ComingSoon = lazy(() => import("./component/ComingSoon"));
const ShortTermLoan = lazy(() => import("./component/ShortTermLoan"));
const PersonalLoanSalaried = lazy(() => import("./component/PersonalLoanSalaried"));
const QuickPersonalLoan = lazy(() => import("./component/QuickPersonalLoan"));
const OnlinePersonalLoan = lazy(() => import("./component/OnlinePersonalLoan"));
const PersonalLoan50k = lazy(() => import("./component/PersonalLoan50k"));
const PersonalLoanEligibility = lazy(() => import("./component/PersonalLoanEligibility"));
const DocumentsRequired = lazy(() => import("./component/DocumentsRequired"));
const MumbaiShortTermLoan = lazy(() => import("./component/city/MumbaiShortTermLoan"));
const DelhiShortTermLoan = lazy(() => import("./component/city/DelhiShortTermLoan"));
const BengaluruShortTermLoan = lazy(() => import("./component/city/BengaluruShortTermLoan"));
const PuneShortTermLoan = lazy(() => import("./component/city/PuneShortTermLoan"));
const AhmedabadShortTermLoan = lazy(() => import("./component/city/AhmedabadShortTermLoan"));
const HyderabadShortTermLoan = lazy(() => import("./component/city/HyderabadShortTermLoan"));

const ExternalRedirect = ({ to }: { to: string }) => {
  const hasRedirected = useRef(false);
  useEffect(() => {
    if (!hasRedirected.current) {
      hasRedirected.current = true;
      window.location.href = to;
    }
  }, [to]);
  return null;
};

function App() {
  return (
    <>
      <ScrollToTop />
      <AnalyticsTracker />
      <Suspense fallback={null}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<AboutUs />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/contact" element={<ContactUs />} />
          <Route path="/repay" element={<ExternalRedirect to={`${import.meta.env.VITE_WEBSITE_LINK}/repay-now`} />} />
          <Route path="/blogs" element={<Blogs />} />
          <Route path="/blogs/:slug" element={<BlogDetail />} />
          <Route path="/loan-details" element={<LoanIllustrationAndRates />} />
          <Route path="/loan-illustrations" element={<LoanIllustrationAndRates />} />
          <Route path="/loan-calculator" element={<LoanCalculator />} />
          <Route path="/products" element={<Services />} />
          <Route path="/terms-condition" element={<TermsConditions />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/interest-rate-policy" element={<InterestRatePolicy />} />
          <Route path="/risk-management-policy" element={<RiskManagementPolicy />} />
          <Route path="/cancellation-refund-policy" element={<CancellationRefundPolicy />} />
          <Route path="/fair-practice-code" element={<FairPracticeCode />} />
          <Route path="/corporate-governance" element={<CorporateGovernance />} />
          <Route path="/kyc-aml-policy" element={<KYCAmlPolicy />} />
          <Route path="/coming-soon" element={<ComingSoon />} />
          <Route path="/short-term-loan" element={<ShortTermLoan />} />
          <Route path="/short-term-loan/mumbai" element={<MumbaiShortTermLoan />} />
          <Route path="/short-term-loan/delhi" element={<DelhiShortTermLoan />} />
          <Route path="/short-term-loan/bengaluru" element={<BengaluruShortTermLoan />} />
          <Route path="/short-term-loan/pune" element={<PuneShortTermLoan />} />
          <Route path="/short-term-loan/ahmedabad" element={<AhmedabadShortTermLoan />} />
          <Route path="/short-term-loan/hyderabad" element={<HyderabadShortTermLoan />} />
          <Route path="/short-term-loan/:city" element={<Navigate to="/short-term-loan" replace />} />
          <Route path="/personal-loan-salaried-professionals" element={<PersonalLoanSalaried />} />
          <Route path="/quick-personal-loan" element={<QuickPersonalLoan />} />
          <Route path="/online-personal-loan" element={<OnlinePersonalLoan />} />
          <Route path="/personal-loan-up-to-50000" element={<PersonalLoan50k />} />
          <Route path="/personal-loan-eligibility" element={<PersonalLoanEligibility />} />
          <Route path="/documents-required-for-personal-loan" element={<DocumentsRequired />} />
        </Routes>
      </Suspense>
      <SafetyTipsBar />
    </>
  );
}

export default App;
